// Displayed when onboardedOn=null (RegistrationCompleted -> OnboardingScreen)

import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router';
import * as companyActions from 'features/company/companyActions';
import imgCheckmark from 'img/registration/checkmark-blue.svg';
import imgHelpdesk from 'img/registration/helpdesk.svg';
import LoadingSpinner from 'components/LoadingSpinner';
import NoAccessMessage from 'components/NoAccessMessage';
import { ReduxState, Status } from 'types';
import { authSlice } from 'features/auth/authSlice';
import { SplitPageTemplate } from './SplitPageTemplate';


const TrackAndTraceOnboardingSteps = () => (
    <div className='pad-2-0'>
        <img src={imgCheckmark} className='mb-05' alt='check' />
        <h1 className='title-black'><FormattedMessage id='ONBOARDING_V2_TITLE' /></h1>
        <p className='t-20 pre-line'><FormattedMessage id='ONBOARDING_V2_SUBTITLE' /></p>

        <div className='step-box'>
            <div className='step-box-circle'>1</div>
            <div className='step-box-content'>
                <h3 className='t-20-500'><FormattedMessage id='ONBOARDING_V2_STEP_1_TITLE' /></h3>
                <p className='t-20'><FormattedMessage id='ONBOARDING_V2_STEP_1_TEXT' /></p>
            </div>
        </div>

        <div className='step-box'>
            <div className='step-box-circle'>2</div>
            <div className='step-box-content'>
                <h3 className='t-20-500'><FormattedMessage id='ONBOARDING_V2_STEP_2_TITLE' /></h3>
                <p className='t-20'><FormattedMessage id='ONBOARDING_V2_STEP_2_TEXT' /></p>
            </div>
        </div>

        <div className='step-box'>
            <div className='step-box-circle'>3</div>
            <div className='step-box-content'>
                <h3 className='t-20-500'><FormattedMessage id='ONBOARDING_V2_STEP_3_TITLE' /></h3>
                <p className='t-20'><FormattedMessage id='ONBOARDING_V2_STEP_3_TEXT' /></p>
            </div>
        </div>

        <div className='step-box-blue'>
            <div className='step-box-circle-blue'>
                <img src={imgHelpdesk} alt='helpdesk icon' />
            </div>
            <div className='step-box-content'>
                <h3 className='t-20-700-black'><FormattedMessage id='ONBOARDING_V2_STEP_SUPPORT_TITLE' /></h3>
                <p className='t-20-400-blue'><FormattedMessage id='ONBOARDING_V2_STEP_SUPPORT_TEXT' /></p>
            </div>
        </div>
    </div>
)


export const OnboardingScreen = (props: OnboardingScreenProps) => {

    const navigate = useNavigate();
    const { onboardedOn, companyGetStatus, getCompanyDetails } = props;

    useEffect(() => {
        if (onboardedOn) {
            navigate('/vehicles');
        }
    }, [onboardedOn]);

    useEffect(() => {
        getCompanyDetails();
    }, []);

    // TODO PD-6034: If I understand correctly this is used to check if a regular user (no FM) tries to log into MyParkd.
    // On that case, the API returns 403, not 400; so this check would let it pass anyway.
    // I also think this method should be renamed to something more accurate, like checkFleetManagerRights

    // - Tim: we already handle 403 on a global level. I don't remember why this was introduced here...
    // seems very unlikely a regular user would access this url directly
    const checkFleetManagerRights = (companyGetStatus: Status): boolean => {
        return companyGetStatus.error?.response?.statusCode === 400;
    };

    const userIsMobileAppUser = checkFleetManagerRights(companyGetStatus);

    if (userIsMobileAppUser) {
        return <NoAccessMessage logout={props.logout} />;
    }

    if (companyGetStatus.loading) {
        return (
            <SplitPageTemplate
                LeftContent={<LoadingSpinner />}
            />
        )
    }

    if (companyGetStatus.succeeded && !onboardedOn) {
        return (
            <SplitPageTemplate
                LeftContent={<TrackAndTraceOnboardingSteps />}
            />
        )
    }

    return null;
};


type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type OnboardingScreenProps = StateProps & DispatchProps;

function mapStateToProps(state: ReduxState) {
    return {
        companyGetStatus: state.companyDetails?.companyGetStatus,
        onboardedOn: state.companyDetails?.companyDetails?.onboardedOn
    };
}

function mapDispatchToProps(dispatch: any) {
    return bindActionCreators({
        getCompanyDetails: companyActions.getCompanyDetails,
        logout: authSlice.actions.logout,
    }, dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(OnboardingScreen);